import {IconButton} from '@wandb/components';
import React, {useEffect, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {getPanelSpec, PanelType} from '../../../util/panelTypes';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useGoToBaseUrl} from '../hooks/hooks';
import {usePanelContext} from '../PanelContextProvider';
import {FullScreenPanelEditButton} from '../PanelEditButton';
import {FullScreenPanelRunMetricButton} from '../PanelRunMetricButton';
import {FullScreenPanelOverflowMenu} from './FullScreenPanelOverflowMenu';

export const FullScreenPanelHeader = ({
  panelBankSectionConfigRef,
  panelType,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  panelType: PanelType;
}) => {
  const {panelRef, panelPopupMode} = usePanelContext();
  const goToBaseURL = useGoToBaseUrl();
  const workspaceDrawerContext = useWorkspaceDrawerContext();

  const panelSpec = useMemo(() => getPanelSpec(panelType), [panelType]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Don't navigate back if a drawer is open - let the drawer handle ESC
        if (workspaceDrawerContext?.contentType) {
          return;
        }
        goToBaseURL();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [goToBaseURL, panelPopupMode, workspaceDrawerContext?.contentType]);

  return (
    <div
      className={twMerge(
        'flex shrink-0',
        // since we have more real estable in full screen mode, we should add more spacing between panel header and panel content itself
        'mb-18 min-h-[32px]'
      )}
      data-test="panel-header">
      <div className="flex w-full justify-between">
        <IconButton
          aria-label="Back"
          icon="back"
          variant="ghost"
          onPress={e => {
            e.continuePropagation();
            goToBaseURL();
          }}
        />
        <div className="flex gap-8">
          <FullScreenPanelRunMetricButton
            panelBankSectionConfigRef={panelBankSectionConfigRef}
            panelRef={panelRef}
            panelType={panelType}
          />
          <FullScreenPanelEditButton />
          <FullScreenPanelOverflowMenu
            panelBankSectionConfigRef={panelBankSectionConfigRef}
            panelSpec={panelSpec}
          />
        </div>
      </div>
    </div>
  );
};
